﻿import { normalizeSearchText } from "@js/core/utils/search";

function getCardRects(cards) {
    const map = new Map();

    cards.forEach((card) => {
        if (card.style.display === "none") return;
        map.set(card, card.getBoundingClientRect());
    });

    return map;
}

function animateCardLayout(cards, firstRects) {
    cards.forEach((card) => {
        if (card.style.display === "none") return;

        const first = firstRects.get(card);
        const last = card.getBoundingClientRect();

        if (!first || !last) return;

        const dx = first.left - last.left;
        const dy = first.top - last.top;

        if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;

        card.style.transition = "none";
        card.style.transform = `translate(${dx}px, ${dy}px)`;

        requestAnimationFrame(() => {
            card.style.transition =
                "transform 760ms cubic-bezier(.16, 1, .3, 1), opacity 360ms ease, filter 480ms ease";
            card.style.transform = "";
        });
    });
}

function hideCardAnimated(card) {
    return new Promise((resolve) => {
        if (card.style.display === "none") {
            resolve();
            return;
        }

        let finished = false;

        const done = () => {
            if (finished) return;
            finished = true;

            card.style.display = "none";
            card.classList.remove("is-filter-hiding");
            card.removeEventListener("transitionend", onEnd);
            resolve();
        };

        const onEnd = (e) => {
            if (e.target !== card) return;
            done();
        };

        card.classList.add("is-filter-hiding");
        card.addEventListener("transitionend", onEnd);

        window.setTimeout(done, 520);
    });
}

function showCardAnimated(card, index = 0) {
    if (card.style.display !== "none") return;

    card.style.display = "";
    card.classList.add("is-filter-showing");
    card.style.transitionDelay = `${Math.min(index * 45, 220)}ms`;

    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            card.classList.remove("is-filter-showing");

            window.setTimeout(() => {
                card.style.transitionDelay = "";
            }, 850);
        });
    });
}

export async function initEventsPage() {
    const searchInput = document.getElementById("eventSearchInput");
    const clearSearchButton = document.getElementById("clearEventSearch");
    const eventCards = Array.from(document.querySelectorAll(".event-card"));
    const visibleEventCount = document.getElementById("visibleEventCount");
    const emptyState = document.getElementById("eventsEmptyState");
    const resetButton = document.getElementById("resetEventFiltersBtn");
    const yearButtons = document.querySelectorAll("[data-year-filter]");
    const sidebarTagButtons = document.querySelectorAll("[data-tag-sidebar]");
    const cardTagButtons = document.querySelectorAll("[data-tag-filter]");

    if (!eventCards.length) return;

    let activeYear = "";
    let activeTag = "";
    let filterRunId = 0;

    function updateClearVisibility() {
        if (!searchInput || !clearSearchButton) return;
        clearSearchButton.classList.toggle("is-visible", searchInput.value.trim().length > 0);
    }

    async function applyFilters() {
        const runId = ++filterRunId;
        const term = normalizeSearchText(searchInput?.value || "");

        const decisions = eventCards.map((card) => {
            const haystack = normalizeSearchText(card.dataset.search || "");
            const year = card.dataset.year || "";
            const tagsRaw = card.dataset.tags || "";
            const tags = tagsRaw
                .split(",")
                .map((x) => normalizeSearchText(x))
                .filter(Boolean);

            const matchesTerm =
                !term ||
                haystack.includes(term) ||
                haystack.replace(/\s+/g, "").includes(term.replace(/\s+/g, ""));

            const matchesYear = !activeYear || year === activeYear;
            const matchesTag = !activeTag || tags.includes(normalizeSearchText(activeTag));

            return {
                card,
                shouldShow: matchesTerm && matchesYear && matchesTag,
                isVisible: card.style.display !== "none"
            };
        });

        const toHide = decisions
            .filter((x) => !x.shouldShow && x.isVisible)
            .map((x) => x.card);

        const toShow = decisions
            .filter((x) => x.shouldShow && !x.isVisible)
            .map((x) => x.card);

        const finalVisibleCount = decisions.filter((x) => x.shouldShow).length;

        // 1) Önce gizlenecekleri çıkar
        await Promise.all(toHide.map((card) => hideCardAnimated(card)));

        // Eski filtre koşusuysa devam etme
        if (runId !== filterRunId) return;

        // Grid reflow için bir frame bekle
        await new Promise((resolve) => requestAnimationFrame(resolve));

        // 2) Mevcut görünür kartların eski pozisyonlarını al
        const firstRects = getCardRects(eventCards);

        // 3) Sonra yeni gösterilecekleri aç
        let showIndex = 0;
        toShow.forEach((card) => {
            showCardAnimated(card, showIndex++);
        });

        // 4) Yeni yerleşime akıt
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                if (runId !== filterRunId) return;
                animateCardLayout(eventCards, firstRects);
            });
        });

        if (visibleEventCount) {
            visibleEventCount.textContent = String(finalVisibleCount);
        }

        if (emptyState) {
            emptyState.classList.toggle("is-visible", finalVisibleCount === 0);
        }
    }

    function setActiveYear(year) {
        activeYear = year || "";

        yearButtons.forEach((btn) => {
            btn.classList.toggle("is-active", (btn.dataset.yearFilter || "") === activeYear);
        });

        applyFilters();
    }

    function setActiveTag(tag) {
        activeTag = tag || "";

        sidebarTagButtons.forEach((btn) => {
            btn.classList.toggle("is-active", (btn.dataset.tagSidebar || "") === activeTag);
        });

        cardTagButtons.forEach((btn) => {
            btn.classList.toggle("is-active", (btn.dataset.tagFilter || "") === activeTag);
        });

        applyFilters();
    }

    if (searchInput) {
        searchInput.addEventListener("input", function () {
            updateClearVisibility();
            applyFilters();
        });
    }

    if (clearSearchButton) {
        clearSearchButton.addEventListener("click", function () {
            if (!searchInput) return;

            searchInput.value = "";
            updateClearVisibility();
            applyFilters();
            searchInput.focus();
        });
    }

    yearButtons.forEach((btn) => {
        btn.addEventListener("click", function () {
            setActiveYear(this.dataset.yearFilter || "");
        });
    });

    sidebarTagButtons.forEach((btn) => {
        btn.addEventListener("click", function () {
            const selectedTag = this.dataset.tagSidebar || "";
            setActiveTag(activeTag === selectedTag ? "" : selectedTag);
        });
    });

    cardTagButtons.forEach((btn) => {
        btn.addEventListener("click", function () {
            const selectedTag = this.dataset.tagFilter || "";
            setActiveTag(activeTag === selectedTag ? "" : selectedTag);
        });
    });

    if (resetButton) {
        resetButton.addEventListener("click", function () {
            if (searchInput) {
                searchInput.value = "";
            }

            activeYear = "";
            activeTag = "";

            updateClearVisibility();
            setActiveYear("");
            setActiveTag("");
            applyFilters();
        });
    }

    updateClearVisibility();
    await applyFilters();
}